/* eslint-disable complexity */
/* eslint-disable max-lines */
import {
    CogIcon,
    HeroChartBarSquareIcon,
    HeroSquares2x2plusIcon,
    HeroTicketIcon,
    HeroVideoCameraIconSolid,
    LockClosedIcon,
    LockOpenIcon,
    MailIcon,
    ReplyIcon,
} from '../../Icon'
import {
    Dropdown,
    DropdownButtonNew as DropdownButton,
    DropdownContent,
} from '../../Dropdown'
import {
    getActiveQueueAssignableTicketCount,
    getActiveQueueRegisteredTicketCount,
    getActiveQueueSettings,
} from 'utils/queueSettings'
import {
    styledButton,
    styledContent,
    styledList,
    styledName,
    styledStatus,
} from 'components/Sidebar/styles'
import {
    useQueueDuration,
    useTicketDuration,
} from 'hooks/queueTime'
import Button from '../../Button'
import {Icon} from 'components/Icon/types'
import QueueAvatar from '../../Avatar/QueueAvatar'
import QueueingTimeView from 'components/QueueingTime'
import React from 'react'
import SidebarAudiences from '../../SidebarAudiences'
import {SidebarQueueViewProps} from './types'
import SidebarTranscripts from 'components/SidebarTranscripts'
import {getGroupQueues} from 'selectors/ninchat/groups'
import {navigation} from 'navigation'
import permissionGuard from 'components/Navigation/helpers/PermissionGuard'
import {styled} from 'stitches/config'
import {styledItem} from 'components/Dropdown/styles'
import {styledTicket} from './styles'
import {useAppSelector} from 'hooks'
import {useEnsureTicketDurationData} from 'hooks/queueTime/useEnsureTicketDurationData'
import {useTranslation} from 'react-i18next'
const StyledList = styled('li', styledList)
const StyledContent = styled('div', styledContent)
const StyledButton = styled('button', styledButton)
const StyledName = styled('div', styledName, {variants: {closed: {true: {opacity: '0.5'}}}})
const StyledStatus = styled('div', styledStatus)
const StyledItem = styled('div', styledItem)
const StyledTicket = styled('div', styledTicket)

const SidebarQueueView = (props: SidebarQueueViewProps): React.ReactElement => {
    const {t} = useTranslation()

    const hasViewRealmStatisticsRights = permissionGuard.hasViewRealmStatisticsPermissions()
    const hasUpdateQueueClosedPermissions = permissionGuard.hasUpdateQueueClosedPermissions()
    const hasViewRealmQueuePermissions = permissionGuard.hasViewRealmQueuePermissions()
    const hasManageRealmQueuePermissions = permissionGuard.hasManageRealmQueuePermissions()

    const {
        onAcceptAudience,
        onToggleQueue,
        queue_attrs,
        queue_id,
        queue_settings,
        loadQueue,
        loadQueueSettings,
        loadQueueStatistics,
        isSelected,
        loading,
        nestedSidebar,
        onAssignOldestTranscript,
    } = props

    const isGroupQueue = !!queue_attrs?.is_group
    const groupQueues = useAppSelector((state) => getGroupQueues(state, isGroupQueue, queue_id))
    const queueForSettings = {
        queue_attrs,
        queue_settings,
    }
    const activeQueueSettings = getActiveQueueSettings({queue: queueForSettings})
    const getGroupQueueActiveSettings = (groupQueue: ReturnType<typeof getGroupQueues>[number]) => getActiveQueueSettings({
        parentGroupQueue: queueForSettings,
        queue: groupQueue.queueProps,
    })
    const activeGroupQueueSettings = groupQueues.map(getGroupQueueActiveSettings)
    const isQueuePickupDelayed = useAppSelector(({ui}) => {
        const isDirectQueuePickupDelayed = !!ui.queue_pickup_delays?.[queue_id]
        const isGroupQueuePickupDelayed = isGroupQueue && groupQueues.some(
            (groupQueue) => !!ui.queue_pickup_delays?.[groupQueue.id],
        )

        return isDirectQueuePickupDelayed || isGroupQueuePickupDelayed
    })
    const queueDuration = useQueueDuration(queue_id)
    const ticketDuration = useTicketDuration(queue_id)
    const groupQueueRegisteredContacts = groupQueues.reduce((acc, curr) => acc + getActiveQueueRegisteredTicketCount({
        parentGroupQueue: queueForSettings,
        queue: curr.queueProps,
    }), 0)
    const assignOldestEnabled = isGroupQueue
        ? groupQueues.some((groupQueue) => getGroupQueueActiveSettings(groupQueue)?.assign_oldest_ticket)
        : activeQueueSettings?.assign_oldest_ticket ?? false
    const registeredTicketCount = isGroupQueue
        ? groupQueueRegisteredContacts
        : getActiveQueueRegisteredTicketCount({queue: queueForSettings})
    const assignOldestTicketCount = isGroupQueue
        ? groupQueues.reduce((acc, groupQueue) => acc + getActiveQueueAssignableTicketCount({
            parentGroupQueue: queueForSettings,
            queue: groupQueue.queueProps,
        }), 0)
        : getActiveQueueAssignableTicketCount({queue: queueForSettings})
    const unassignedTicketCount = registeredTicketCount
    const sidebarQueueCounter = activeQueueSettings?.sidebar_queue_counter ?? 'queueing_time'
    const effectiveSidebarQueueCounter = sidebarQueueCounter
    const showQueueTimer = effectiveSidebarQueueCounter === 'queueing_time'
    const showTicketTimer = effectiveSidebarQueueCounter === 'ticket_pending_time'
    const queueDurationSourceQueue = queueDuration.sourceQueueId
        ? groupQueues.find((groupQueue) => groupQueue.id === queueDuration.sourceQueueId)
        : undefined
    const queueDurationSourceSettings = queueDurationSourceQueue
        ? getGroupQueueActiveSettings(queueDurationSourceQueue)
        : undefined
    const queueTimerKey = `queue:${queueDuration.sourceQueueId ?? queue_id}`
    const ticketDurationSourceQueue = ticketDuration.sourceQueueId
        ? groupQueues.find((groupQueue) => groupQueue.id === ticketDuration.sourceQueueId)
        : undefined
    const ticketDurationSourceSettings = ticketDurationSourceQueue
        ? getGroupQueueActiveSettings(ticketDurationSourceQueue)
        : undefined
    const queueSlaSettings = isGroupQueue
        ? queueDurationSourceQueue
            ? queueDurationSourceSettings
            : activeGroupQueueSettings.find((settings) => settings?.queue_sla)
        : activeQueueSettings
    const ticketSlaSettings = isGroupQueue
        ? ticketDurationSourceQueue
            ? ticketDurationSourceSettings
            : activeGroupQueueSettings.find((settings) => settings?.ticket_sla)
        : activeQueueSettings
    useEnsureTicketDurationData(queue_id, showTicketTimer)
    const showTicketDuration = showTicketTimer && ticketDuration.isLoaded === true && ticketDuration.queue_time > 0

    const highlightTickets = () => unassignedTicketCount > 0

    // not display group queues if there are not any queues in the group
    if (isGroupQueue && groupQueues.length === 0) {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <></>
    }

    return (
        <StyledList>
            <StyledContent>
                <StyledButton
                    active={isSelected}
                    onClick={(): void => {
                        loadQueue(queue_id)
                    }}
                    type='button'
                >
                    <QueueAvatar
                        closed={queue_attrs?.closed}
                        groupQueues={groupQueues}
                        isGroupQueue={isGroupQueue}
                        length={isQueuePickupDelayed ? 0 : isGroupQueue ? queueDuration.queueLength : queue_attrs?.length}
                        name={queue_attrs?.name}
                        size='small'
                        unassigned_registered={highlightTickets()}
                    />
                    <StyledName
                        closed={queue_attrs?.closed}
                        status={(isQueuePickupDelayed ? 0 : isGroupQueue ? queueDuration.queueLength : queue_attrs?.length) || highlightTickets() ? 'highlight' : undefined}
                    >{queue_attrs?.name}
                    </StyledName>
                    {!isQueuePickupDelayed && highlightTickets() &&
                        <StyledTicket><HeroTicketIcon size={18} /><span>{unassignedTicketCount}</span></StyledTicket>}
                    {queue_attrs.video === 'group' &&
                        <StyledStatus><span title={t('Video queue')}><HeroVideoCameraIconSolid size={14} /></span></StyledStatus>}
                    {showQueueTimer && !isQueuePickupDelayed ?
                        <QueueingTimeView
                            delivery_time={queueDuration.delivery_time}
                            queueLength={queueDuration.queueLength}
                            queue_time={queueDuration.queue_time}
                            sla={queueSlaSettings?.queue_sla}
                            slaReminderTime={queueSlaSettings?.queue_sla_reminder_time}
                            slaWarningTime={queueSlaSettings?.queue_sla_warning_time}
                            timerKey={queueTimerKey}
                        />
                        : null}
                    {!isQueuePickupDelayed && showTicketDuration ?
                        <QueueingTimeView
                            queueLength={ticketDuration.queueLength}
                            queue_time={ticketDuration.queue_time}
                            sla={ticketSlaSettings?.ticket_sla}
                            slaReminderTime={ticketSlaSettings?.ticket_sla_reminder_time}
                            slaWarningTime={ticketSlaSettings?.ticket_sla_warning_time}
                        />
                        : null}
                </StyledButton>

                <Dropdown>
                    <DropdownButton caret={true} color='text' data-id='dropdown-caret' size='small' />
                    {isGroupQueue
                        ? (
                            <DropdownContent direction='fixed-end'>
                                <StyledItem>
                                    <Button
                                        color='text'
                                        disabled={isQueuePickupDelayed || !queueDuration.queueLength}
                                        iconLeft={ReplyIcon}
                                        onPress={(): void => { onAcceptAudience(queue_id) }}
                                        text={t('Pick next from group')}
                                        type='button'
                                    />
                                </StyledItem>
                                {assignOldestEnabled
                                    ? (
                                        <StyledItem>
                                            <Button
                                                color='text'
                                                disabled={isQueuePickupDelayed || loading || !assignOldestTicketCount}
                                                iconLeft={MailIcon}
                                                onPress={onAssignOldestTranscript}
                                                text={t('Assign to next ticket')}
                                                type='button'
                                            />
                                        </StyledItem>
                                    )
                                    // eslint-disable-next-line react/jsx-no-useless-fragment
                                    : <></>}
                                <StyledItem>
                                    <Button
                                        color='text'
                                        iconLeft={HeroSquares2x2plusIcon as Icon}
                                        onPress={(): void => { navigation.push('Queue', {queue_id}) }}
                                        text={t('Show queue group')}
                                        type='button'
                                    />
                                </StyledItem>
                            </DropdownContent>
                        ) :
                        (
                            <DropdownContent direction='fixed-end'>
                                <StyledItem>
                                    <Button
                                        color='text'
                                        disabled={isQueuePickupDelayed || (isGroupQueue ? queueDuration.queueLength === 0 : !queue_attrs?.length)}
                                        iconLeft={ReplyIcon}
                                        onPress={(): void => { onAcceptAudience(queue_id) }}
                                        text={t('Pick customer from queue')}
                                        type='button'
                                    />
                                </StyledItem>
                                {assignOldestEnabled
                                    ? (
                                        <StyledItem>
                                            <Button
                                                color='text'
                                                disabled={isQueuePickupDelayed || loading || !unassignedTicketCount}
                                                iconLeft={MailIcon}
                                                onPress={onAssignOldestTranscript}
                                                text={t('Assign to next ticket')}
                                                type='button'
                                            />
                                        </StyledItem>
                                    )
                                    // eslint-disable-next-line react/jsx-no-useless-fragment
                                    : <></>}
                                {(!!hasUpdateQueueClosedPermissions || !!hasManageRealmQueuePermissions) &&
                                <StyledItem>
                                    {!!queue_attrs?.closed &&
                                        <Button
                                            color='text'
                                            disabled={queue_attrs?.suspended}
                                            iconLeft={LockOpenIcon}
                                            onPress={(): void => { onToggleQueue(queue_id, Boolean(queue_attrs?.closed)) }}
                                            text={t('Open queue')}
                                            type='button'
                                        />}
                                    {!queue_attrs?.closed &&
                                        <Button
                                            color='text'
                                            disabled={queue_attrs?.suspended}
                                            iconLeft={LockClosedIcon}
                                            onPress={(): void => { onToggleQueue(queue_id, Boolean(queue_attrs?.closed)) }}
                                            text={t('Close queue')}
                                            type='button'
                                        />}
                                </StyledItem>}
                                {!!hasViewRealmQueuePermissions &&
                                <StyledItem>
                                    <Button
                                        color='text'
                                        iconLeft={CogIcon}
                                        onPress={(): void => { loadQueueSettings(queue_id) }}
                                        text={t('Queue settings')}
                                        type='button'
                                    />
                                </StyledItem>}
                                {!!hasViewRealmStatisticsRights &&
                                <StyledItem>
                                    <Button
                                        color='text'
                                        iconLeft={HeroChartBarSquareIcon as unknown as Icon}
                                        onPress={(): void => { loadQueueStatistics(queue_id) }}
                                        text={t('Queue statistics')}
                                        type='button'
                                    />
                                </StyledItem>}
                            </DropdownContent>
                        )}
                </Dropdown>
            </StyledContent>
            {Boolean(nestedSidebar) && <SidebarAudiences queue_attrs={queue_attrs} queue_id={queue_id} /> }
            <SidebarTranscripts
                parentQueueSettings={isGroupQueue ? queue_settings : undefined}
                queue_attrs={queue_attrs}
                queue_id={queue_id}
            />
        </StyledList>
    )
}

export default SidebarQueueView
